<script setup lang="ts">
import { computed } from 'vue';

import TemplateService from '@/core/conversations/template/template.service';
import TrashBigIcon from '@/assets/svg/conversations/TrashBigIcon.svg?component';
import PenIcon from '@/assets/svg/conversations/PenIcon.svg?component';
import type { TemplatePreviewContext } from '@/core/conversations/template/template.utils';

const templateService = new TemplateService();

const props = withDefaults(
  defineProps<{
    id: number;
    title: string;
    templateText: string;
    /** Owned by the current user, so it can be edited and deleted. */
    custom?: boolean;
    /**
     * `raw` highlights the placeholder tokens as-is; `personalized` renders the
     * substituted values. Independent of which actions are offered — Talent Search needs
     * a personalized preview with no Send button but full edit/delete.
     */
    previewMode?: 'raw' | 'personalized';
    /** Offer "Send" (immediate send). Off where sending belongs to the surrounding flow. */
    showSend?: boolean;
    /** Offer Edit/Delete for a custom template. */
    canManage?: boolean;
    /**
     * Explicit values for a `personalized` preview. Pass this wherever there is no selected
     * conversation, otherwise mapTemplatePlaceholders falls back to the conversation index
     * and can render an unrelated candidate's name.
     */
    previewContext?: TemplatePreviewContext;
  }>(),
  {
    previewMode: 'personalized',
    showSend: true,
    canManage: true,
  },
);

const emits = defineEmits<{
  (e: 'insertTemplate', templateId: number): void;
  (e: 'sendTemplate', templateId: number): void;
  (e: 'deleteTemplate', templateId: number): void;
  (e: 'editTemplate', templateId: number): void;
}>();

const previewHtml = computed(() => {
  if (props.previewMode === 'raw') {
    return templateService.mapTemplatePlaceholdersForBulkMessaging(props.templateText);
  }
  return templateService.mapTemplatePlaceholders(
    props.templateText,
    true,
    props.previewContext?.proFullName,
    props.previewContext?.jobTitle,
    props.previewContext?.proFirstName,
  );
});

const showManageActions = computed(() => props.canManage && props.custom === true);

const insertTemplate = () => {
  emits('insertTemplate', props.id);
};

const sendTemplate = () => {
  emits('sendTemplate', props.id);
};

const deleteTemplate = () => {
  emits('deleteTemplate', props.id);
};

const editTemplate = () => {
  emits('editTemplate', props.id);
};
</script>

<template>
  <div
    class="flex h-full flex-col space-y-2 rounded-lg border border-tint-80 p-4"
    data-test="template-card"
  >
    <div class="flex justify-between">
      <h3 class="text-sm font-bold text-shade-900" data-test="template-card-title">{{ title }}</h3>
      <img
        v-if="!custom"
        src="@/assets/svg/ff-logo-white.svg"
        alt="FactoryFix"
        class="h-4 w-[18px]"
      />
    </div>
    <p class="text-sm text-black" data-test="template-card-preview" v-html="previewHtml"></p>

    <div class="flex flex-1 items-end">
      <div class="flex w-full items-center justify-between">
        <v-tooltip
          v-if="showManageActions"
          location="top"
          content-class="project-card-tooltip tooltip-bottom-arrow"
        >
          <template #activator="{ props: tooltipProps }">
            <PenIcon
              class="mr-2 h-6 w-6 cursor-pointer text-shade-900"
              data-test="template-card-edit"
              v-bind="tooltipProps"
              @click="editTemplate"
            />
          </template>
          <span class="text-xs text-white">Edit</span>
        </v-tooltip>
        <v-tooltip
          v-if="showManageActions"
          location="top"
          content-class="project-card-tooltip tooltip-bottom-arrow"
        >
          <template #activator="{ props: tooltipProps }">
            <TrashBigIcon
              class="h-6 w-6 cursor-pointer text-shade-900"
              data-test="template-card-delete"
              v-bind="tooltipProps"
              @click="deleteTemplate"
            />
          </template>
          <span class="text-xs text-white">Delete</span>
        </v-tooltip>
        <div class="ml-auto flex gap-2 font-sans">
          <button
            class="cursor-pointer rounded-md border-2 border-shade-880 px-3 py-1.5 text-sm font-semibold text-shade-880 text-tint-0 transition-colors hover:bg-tint-40"
            data-test="template-card-insert"
            @click="insertTemplate"
          >
            Insert
          </button>
          <button
            v-if="showSend"
            class="cursor-pointer rounded-md bg-shade-900 px-3 py-1.5 text-sm font-semibold text-tint-0 transition-colors hover:bg-shade-860"
            data-test="template-card-send"
            @click="sendTemplate"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/*
 * Must not be scoped since the component is moved to the beginning of the v-app
 * https://vuetifyjs.com/en/api/v-tooltip/#props-content-class
 */
.project-card-tooltip {
  border-radius: 6px !important;
  padding: 3px 8px !important;
  background-color: #0f172a !important;
}
</style>
